/**
 * Targets, achievement and run rate (§79-§81).
 *
 * The projection is linear on elapsed days — crude, but explainable. Anything
 * cleverer would need seasonality the data has not been shown to support.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { KpiCard } from "@/components/cards/KpiCard";
import { useFilterStore } from "@/store/filters";
import type { AnalyticsEnvelope } from "@/types/api";
import { count, inr, pct } from "@/utils/format";

interface TargetRow {
  id: number;
  level: string;
  entity_label: string;
  metric: string;
  cadence: string;
  period_start: string;
  period_end: string;
  target: number;
  achieved: number;
  achievement_pct: number | null;
  gap: number;
  days_elapsed: number;
  days_remaining: number;
  run_rate_so_far: number;
  required_daily_run_rate: number | null;
  projected_total: number;
  projected_achievement_pct: number | null;
  on_track: boolean | null;
}

const asValue = (metric: string, v: number | null) =>
  v === null || v === undefined ? "—" : metric === "revenue" ? inr(v) : count(Math.round(v));

export function TargetsPage() {
  const params = useFilterStore((s) => s.queryParams());
  const { data, isLoading } = useQuery({
    queryKey: ["targets", params],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope<Record<string, number>>>(
        "/targets", { params });
      return data;
    },
    staleTime: 300_000,
  });

  const rows = (data?.breakdown ?? []) as unknown as TargetRow[];
  const summary = data?.summary as
    { targets: number; on_track: number; behind: number } | undefined;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-[24px] tracking-tight">Targets</h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
          Achievement against target, plus what the remaining days demand. The
          projection assumes the current run rate holds.
        </p>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
        <KpiCard loading={isLoading} label="Targets set" value={count(summary?.targets)} />
        <KpiCard loading={isLoading} label="On track" value={count(summary?.on_track)} />
        <KpiCard loading={isLoading} label="Behind" value={count(summary?.behind)}
                 higherIsBetter={false} />
      </div>

      {isLoading && <div className="skeleton h-48" />}

      {!isLoading && rows.length === 0 && (
        <div className="card p-8 text-center">
          <p className="text-sm text-ink-muted">No targets defined for this period.</p>
          <p className="text-[12.5px] text-ink-faint mt-2 max-w-lg mx-auto">
            Add them in the Django admin under <span className="font-mono">Targets</span>,
            by level (VP, manager, employee, area or organisation), metric and
            period. Achievement is then computed against the same scoped facts
            the dashboards use.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((t) => {
          const achieved = t.achievement_pct ?? 0;
          const projected = t.projected_achievement_pct ?? 0;
          const tone = t.on_track ? "#2F6B3C" : achieved >= 70 ? "#8A6410" : "#9E3B2E";
          return (
            <article key={t.id} className="card p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="label">{t.level} · {t.cadence}</span>
                  <h3 className="font-display font-semibold text-[16px]">
                    {t.entity_label} — {t.metric.replace(/_/g, " ")}
                  </h3>
                </div>
                <div className="text-right">
                  <span className="font-display font-bold text-[22px] tnum" style={{ color: tone }}>
                    {pct(t.achievement_pct)}
                  </span>
                  <div className="text-[12px] text-ink-muted tnum">
                    {asValue(t.metric, t.achieved)} of {asValue(t.metric, t.target)}
                  </div>
                </div>
              </div>

              {/* Achieved bar, with the linear projection ghosted behind it. */}
              <div className="relative h-2 rounded-full bg-surface-alt mt-3 overflow-hidden">
                <div className="absolute inset-y-0 left-0 rounded-full opacity-30"
                     style={{ width: `${Math.min(projected, 100)}%`, background: tone }} />
                <div className="absolute inset-y-0 left-0 rounded-full"
                     style={{ width: `${Math.min(achieved, 100)}%`, background: tone }} />
              </div>

              <dl className="grid gap-2.5 mt-3"
                  style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                {[
                  ["Gap", asValue(t.metric, t.gap)],
                  ["Days remaining", String(t.days_remaining)],
                  ["Run rate so far", `${asValue(t.metric, t.run_rate_so_far)} / day`],
                  ["Required rate", t.required_daily_run_rate === null
                    ? "target met"
                    : `${asValue(t.metric, t.required_daily_run_rate)} / day`],
                  ["Projected", asValue(t.metric, t.projected_total)],
                  ["Projected %", pct(t.projected_achievement_pct)],
                ].map(([label, value]) => (
                  <div key={label} className="border-t border-rule pt-1.5">
                    <dt className="label">{label}</dt>
                    <dd className="font-display font-semibold text-sm tnum mt-0.5">{value}</dd>
                  </div>
                ))}
              </dl>
            </article>
          );
        })}
      </div>
    </div>
  );
}
